/**
 * Domain types for the Krit Deal Engine.
 * Field names mirror the v9 prototype's DEAL() shape so business logic ports 1:1.
 * (Storage/DB layer may snake_case these — see @krit/db.)
 */
import type { ModuleKey, Status, DiligenceStatus, ThesisVerdict } from './constants.js';

/** Weighted module ratings, each 0–100. */
export type Ratings = Record<ModuleKey, number>;

/**
 * An AI-computed pass at the 7 Krit Score module ratings (deals.moduleRatingsAI) — grounded in the
 * deal's own enriched data, each rating paired with a one-line reason, plus one overall "Krit note"
 * synthesising the whole picture for the team. This FILLS the on-screen sliders (still editable,
 * still needs an explicit Save — never writes `deals.ratings` directly) rather than replacing the
 * manual-rating workflow; an analyst can accept, tweak, or ignore it entirely.
 */
export interface ModuleRatingsAI {
  ratings: Ratings; // suggested 0–100 per module
  reasons: Record<ModuleKey, string>; // one line per module: why THIS rating, grounded in the deal's data
  note: string; // 3-6 sentence plain-English synthesis for the team — what stands out, what's thin, the overall read
  fetchedAt: string; // ISO
}

export interface Customer {
  name: string;
  pct: number;
  note?: string;
}

export interface Product {
  name: string;
  pct: number;
  margin: number;
}

export interface TeamMemberBio {
  name: string;
  role: string;
  bg: string;
}

export interface PnlRow {
  y: string;
  rev: number;
  eb: number;
  pat: number;
}

/* ─────────────────────── Full financial statements (data room) ───────────────────────
 * The headline fields above (rev25/pat26/…) and `pnl` stay the deck-level summary the
 * engines score off. `deals.financials` carries the DETAILED statements an analyst
 * uploads from the data room: a P&L, balance sheet and cash flow, each a list of line
 * items with one value per fiscal-year column. Every ratio shown in the app is COMPUTED
 * from these lines (see financials.ts) — never extracted as a number, so it always ties
 * back to a statement line the analyst can check.
 */

/** Canonical line keys the ratio engine understands. Unrecognised lines still display. */
export type FinLineKey =
  // P&L
  | 'revenue' | 'otherIncome' | 'totalIncome' | 'cogs' | 'grossProfit' | 'employeeCost'
  | 'otherExpenses' | 'totalExpenses' | 'ebitda' | 'depreciation' | 'ebit' | 'interest'
  | 'pbt' | 'exceptional' | 'tax' | 'pat' | 'eps'
  // Balance sheet
  | 'shareCapital' | 'reserves' | 'netWorth' | 'longTermDebt' | 'shortTermDebt' | 'totalDebt'
  | 'otherNonCurrentLiabilities' | 'totalLiabilities' | 'netFixedAssets' | 'cwip' | 'investments'
  | 'otherNonCurrentAssets' | 'inventory' | 'receivables' | 'cash' | 'otherCurrentAssets'
  | 'currentAssets' | 'payables' | 'otherCurrentLiabilities' | 'currentLiabilities' | 'totalAssets'
  // Cash flow
  | 'ocf' | 'capex' | 'icf' | 'fincf' | 'fcf' | 'interestPaid' | 'taxPaid' | 'dividend' | 'netCashFlow';

/** One statement line: a label plus one value per year column (null = not stated). */
export interface FinLine {
  label: string; // as printed in the document, e.g. "Revenue from operations"
  key?: FinLineKey; // set when the line maps onto a canonical concept (drives ratios)
  values: (number | null)[]; // aligned 1:1 with FinancialStatements.years
  bold?: boolean; // subtotal / total row — rendered heavier
  indent?: number; // 0–2 nesting depth for display
}

/** One statement (P&L / balance sheet / cash flow) as an ordered list of lines. */
export interface FinStatement {
  lines: FinLine[];
  note?: string; // e.g. "FY26 provisional / management-certified"
}

/**
 * Detailed multi-year financial statements for a deal (deals.financials), normalised to ₹ crore.
 * `years` are the column labels, oldest → newest ("FY22" … "FY26", "FY27E" for projections).
 */
export interface FinancialStatements {
  years: string[];
  unit: string; // display unit after normalisation — always '₹ Cr'
  pnl: FinStatement;
  balanceSheet: FinStatement;
  cashFlow: FinStatement;
  audited?: (boolean | null)[]; // per-year audited flag, when the document states it
  notes?: string[]; // extraction caveats worth showing the analyst
  source?: string; // document the figures came from ("Financials FY22-26.xlsx" / "deck")
  extractedAt?: string; // ISO timestamp of the extraction run
}

/** A computed ratio: one value per year, plus how to read it. */
export interface RatioRow {
  key: string;
  label: string;
  unit: '%' | 'x' | 'days' | '₹Cr';
  values: (number | null)[]; // aligned with RatioAnalysis.years
  dir: 'up' | 'down' | null; // 'up' = higher is better; null = neither
  note?: string; // formula / caveat, shown on hover
}

export interface RatioGroup {
  key: string;
  label: string;
  rows: RatioRow[];
}

/** A single-number summary across the whole span (CAGRs). */
export interface RatioSummary {
  key: string;
  label: string;
  unit: '%' | 'x' | 'days' | '₹Cr';
  value: number | null;
  span: string; // "FY22 → FY26"
}

export interface RatioAnalysis {
  years: string[];
  groups: RatioGroup[];
  summary: RatioSummary[];
}

export interface Risk {
  risk: string;
  prob: string;
  impact: string;
  mit: string;
}

export interface DiligenceItem {
  item: string;
  cat: string;
  status: DiligenceStatus;
  owner?: string;
  due?: string;
}

/**
 * A single enriched field with provenance — an analyst must see WHERE a number came
 * from before it enters an IC memo (SEBI Cat II AIF). Produced by the enrichment agent.
 */
export interface ProvenancedField {
  value: string | number | null;
  sourceUrl: string | null; // the page/deck the value came from ("uploaded deck" for the pitch deck)
  fetchedAt: string; // ISO timestamp
  confidence: 'high' | 'medium' | 'low';
}

/** One section of the multi-section business profile, with its source. */
export interface BusinessSection {
  content: string | null;
  sourceUrl: string | null;
}
/** The narrative sections of the business profile (`summary` is the one-liner). */
export type BusinessSectionKey =
  | 'summary' | 'history' | 'whatTheyMake' | 'businessModel' | 'customersChannel' | 'operations'
  | 'supplyChain' | 'moat' | 'useOfProceeds' | 'tailwinds';
/**
 * Multi-section business profile (deals.businessDetail). `business` stays the short summary.
 * `builtFrom`/`fetchedAt` record how the profile was produced ('deck' = written from the
 * uploaded deck only, 'deck+web' = the deck plus a bounded web search, 'agent' = the deck +
 * full tool-calling enrichment agent) so the card can say so. `sources` lists the web URLs
 * (if any) the profile actually cited.
 */
export type BusinessDetail = { [K in BusinessSectionKey]?: BusinessSection } & {
  builtFrom?: 'deck' | 'deck+web' | 'agent';
  fetchedAt?: string;
  sources?: string[];
};
/** Ordered section keys + labels for rendering the Business card — reads as a storyboard. */
export const BUSINESS_SECTIONS: { key: BusinessSectionKey; label: string }[] = [
  { key: 'history', label: 'History & founding' },
  { key: 'whatTheyMake', label: 'What they make / do' },
  { key: 'businessModel', label: 'Business model & revenue flow' },
  { key: 'customersChannel', label: 'Customers & channel' },
  { key: 'operations', label: 'Operations & footprint' },
  { key: 'supplyChain', label: 'Supply chain & inputs' },
  { key: 'moat', label: 'Moat / differentiation' },
  { key: 'useOfProceeds', label: 'Use of proceeds' },
  { key: 'tailwinds', label: 'Sector tailwinds' },
];
/**
 * The section keys of a profile that actually carry content — the safe way to ask "is this
 * profile blank?" or "how many sections did we write?" (Object.values() would also pick up the
 * builtFrom/fetchedAt metadata, which are not sections).
 */
export const filledBusinessSections = (bd: BusinessDetail | null | undefined): BusinessSectionKey[] =>
  bd ? (['summary', ...BUSINESS_SECTIONS.map((s) => s.key)] as BusinessSectionKey[]).filter((k) => bd[k]?.content?.trim()) : [];

/** A dated milestone in the company's history (deck-first). */
export interface Milestone {
  date: string; // year or date as stated (e.g. "2019", "Mar 2021")
  event: string; // what happened
  sourceUrl?: string | null;
}
/** One row of the product / SKU catalogue. Only fields the deck/web actually state are filled. */
export interface ProductRow {
  name: string;
  what?: string; // what it is / does
  application?: string; // end-use / application
  customerType?: string; // who buys it
  capacity?: string; // installed capacity / volume, as stated
  pricing?: string; // price / realisation, if stated
  revenueShare?: string; // % of revenue, if stated
  stage?: string; // commercial / pilot / planned
}
/** A single at-a-glance operating metric (NOT a financial-statement line). */
export interface KeyNumber {
  label: string; // e.g. "Installed capacity"
  value: string; // e.g. "120,000 tonnes/yr"
}
/**
 * Deck-mined company briefing (deals.businessBrief) — the "read this instead of the PDF" layer:
 * a dated history timeline, a product catalogue, forward plans, capacity/technology, key operating
 * numbers and the analyst's open-questions list. Deck-first; every prose section carries its source;
 * unsupported sections stay null (never fabricated).
 */
export interface BusinessBrief {
  history?: Milestone[];
  products?: ProductRow[];
  plans?: BusinessSection; // plans & roadmap — expansion, new plants/products, targets, what the round funds
  manufacturing?: BusinessSection; // plants, locations, installed vs utilised capacity
  technology?: BusinessSection; // process/tech, patents, approvals, certifications
  customersContracts?: BusinessSection; // named customers, offtake/contract structure, order book
  competitivePosition?: BusinessSection; // positioning vs named competitors (company-level)
  esg?: BusinessSection; // certifications, emissions/sustainability where the deck covers it
  keyNumbers?: KeyNumber[];
  openQuestions?: string[]; // what the deck does NOT answer — the diligence starting list
  fetchedAt?: string;
}
/** The prose (BusinessSection) keys of BusinessBrief — the narrative sections. */
export type BriefProseKey = 'plans' | 'manufacturing' | 'technology' | 'customersContracts' | 'competitivePosition' | 'esg';
/** Ordered prose-section keys + labels for rendering the briefing's narrative sections. */
export const BRIEF_SECTIONS: { key: BriefProseKey; label: string }[] = [
  { key: 'plans', label: 'Plans & roadmap' },
  { key: 'manufacturing', label: 'Manufacturing & capacity' },
  { key: 'technology', label: 'Technology & IP' },
  { key: 'customersContracts', label: 'Customers & contracts' },
  { key: 'competitivePosition', label: 'Competitive position' },
  { key: 'esg', label: 'ESG & compliance' },
];

/** One "From the internet" item surfaced for the industry deep-dive (news / company site / report). */
export interface IndustryLink {
  title: string;
  url: string;
  source: string; // publisher / hostname
  date: string; // ISO or human date if known, else ''
}

/**
 * Deep-dive, India-specific industry & market analysis (deals.industryDetail).
 * Free-form narrative blocks are `string | null` (null = not supported by a source);
 * bullet blocks are string[]. Never invent market-size figures without a source — an
 * unsupported field stays null/[]. `sources` are the URLs the analysis actually cited.
 */
/** One glossary entry for the industry primer — a sector term in one line. */
export interface GlossaryTerm {
  term: string;
  def: string;
}
/** "What this peer is actually doing now" — a per-peer move for the industry deep-dive. */
export interface PeerMove {
  name: string; // the peer / competitor (listed or notable unlisted)
  note: string; // capacity/expansion, new plants/products, margin direction, order book, recent action
  listed?: boolean; // true = listed comparable, false = unlisted competitor
  sourceUrl?: string | null;
}
export interface IndustryDetail {
  marketSize: string | null; // TAM/SAM in ₹ Cr with year basis (sourced)
  cagr: string | null; // CAGR + period basis
  // ── Industry-primer depth layer (written for a reader who knows nothing about the sector) ──
  primer?: string | null; // "Industry 101": what this industry is, how the product is made/delivered, unit-economics basics
  niche?: string | null; // the specific niche this company plays in vs the broad sector
  marketStructure?: string | null; // fragmented vs consolidated, organised vs unorganised, entry barriers, pricing power
  customerAndChannel?: string | null; // who buys, contract structures (OMC tenders/offtake/B2B/exports), payment & WC norms
  inputCosts?: string | null; // key feedstock/raw materials, price volatility, pass-through ability
  technologySubstitution?: string | null; // competing technologies + substitution risk over the hold
  keyMetrics?: string | null; // the 4-6 numbers to judge any company in this industry on, with benchmark ranges
  whyThisIndustryNow?: string | null; // bull case for the INDUSTRY itself (separate from the company)
  whyNotThisIndustry?: string | null; // bear case for the industry itself
  glossary?: GlossaryTerm[]; // 5-10 sector terms, one line each
  peerMoves?: PeerMove[]; // per-peer "what they're doing" — upgrades peerPerformance from one paragraph
  // ── original sections ──
  indianEcosystem: string | null; // India-specific context, India-vs-global
  valueChain: string | null; // value chain + where the company sits
  competitiveLandscape: string | null; // real listed & unlisted players
  policyRegulation: string | null; // PLI / MNRE / SEBI / duties / state incentives (sector-appropriate)
  demandDrivers: string | null;
  peerPerformance: string | null; // how the listed peers are doing (screener numbers)
  geography: string | null; // state-cluster / operating geography context
  cyclicalityRisks: string | null;
  outlook: string | null;
  tailwinds: string[];
  headwinds: string[];
  whatWouldChangeTheThesis: string[];
  fromTheInternet: IndustryLink[]; // recent headlines / company site / reports, with links + dates
  sources: string[]; // every URL cited this run (≥5 where available)
  fetchedAt: string; // ISO
}

/** Ordered narrative-section keys + labels for rendering the industry deep-dive card. */
export const INDUSTRY_SECTIONS: { key: keyof IndustryDetail; label: string }[] = [
  { key: 'primer', label: 'Industry 101 (primer)' },
  { key: 'niche', label: 'The niche this company plays in' },
  { key: 'marketSize', label: 'Market size (TAM / SAM)' },
  { key: 'cagr', label: 'Growth (CAGR)' },
  { key: 'indianEcosystem', label: 'Indian ecosystem · India vs global' },
  { key: 'marketStructure', label: 'Market structure' },
  { key: 'valueChain', label: 'Value chain & where the company sits' },
  { key: 'competitiveLandscape', label: 'Competitive landscape' },
  { key: 'customerAndChannel', label: 'Customers & channel structure' },
  { key: 'inputCosts', label: 'Input costs & pass-through' },
  { key: 'technologySubstitution', label: 'Technology & substitution risk' },
  { key: 'policyRegulation', label: 'Policy & regulation' },
  { key: 'demandDrivers', label: 'Demand drivers' },
  { key: 'keyMetrics', label: 'Key metrics to judge any player on' },
  { key: 'peerPerformance', label: 'How the listed peers are doing' },
  { key: 'geography', label: 'Geography & state clusters' },
  { key: 'cyclicalityRisks', label: 'Cyclicality & risks' },
  { key: 'whyThisIndustryNow', label: 'Why this industry now (bull)' },
  { key: 'whyNotThisIndustry', label: 'Why not — the bear case' },
  { key: 'outlook', label: 'Outlook' },
];

/** Result of one enrichment-agent run (persisted in enrichment_runs.result). */
export interface EnrichmentResult {
  fields: Record<string, ProvenancedField>; // e.g. promoter, founded, employees, facilities, ipoTarget, rating
  businessDetail?: BusinessDetail; // multi-section narrative (deck-first)
  peers: string[];
  sources: string[]; // every URL the agent actually fetched this run
}

export type EnrichmentStatus = 'running' | 'done' | 'error';

/** One researched fact, with its own provenance (source page + optional date). */
export interface ResearchFact {
  label: string; // what the fact is (e.g. "Latest funding round")
  value: string; // the fact itself, grounded in a source — never fabricated
  sourceUrl?: string; // the real fetched URL the fact came from
  date?: string; // ISO or human date, if the source states one
}
/** A titled group of research facts (e.g. "Funding & investors"). */
export interface ResearchSection {
  title: string;
  items: ResearchFact[];
}
/**
 * Open-ended web-research dossier for a deal (deals.research). Assembled by the
 * auto-populate orchestrator from bounded web searches → one structured LLM pass.
 * Every fact carries a source; `sources` are the REAL URLs actually cited this run.
 */
export interface ResearchDossier {
  sections: ResearchSection[];
  sources: string[];
  fetchedAt: string; // ISO
}

/**
 * Cross-call industry-trend synthesis (deals.callsTrend) — the UNDERLYING INDUSTRY read distilled
 * from the deal's own + competitors' earnings-call transcripts (demand, pricing, margins, capex,
 * technology/AI, outlook). Generated on demand from the calls' analyses, cached on the deal.
 */
export interface CallsTrend {
  summary: string; // 3–5 sentence underlying industry trend across the calls
  themes: { label: string; detail: string }[]; // e.g. Demand / Pricing / Margins / AI & tech / Capex / Outlook
  sources: string[]; // titles of the calls that fed the synthesis
  generatedAt: string; // ISO
}

/** A management/board/founder person recognised by the research pass, with source. */
export interface ResearchPerson {
  name: string;
  role: string;
  bg: string; // one-line background ('' if the source doesn't say — never invented)
  sourceUrl: string; // the real fetched URL the person was found on
}
/**
 * The RECOGNISED, structured facts distilled from the same open-ended research pass — a
 * side-output alongside the free-form `sections`, ready to be DISTRIBUTED (blanks-only)
 * into the deal's structured tabs (Team & Mgmt, Compliance, registry card, customers,
 * products). Every field is present ONLY when a real source supported it; nothing is
 * fabricated — unknowns are simply omitted. `customers` carry NO invented concentration %.
 */
export interface ResearchStructured {
  people: ResearchPerson[]; // → intel.leadership (dedup vs directors)
  businessSummary?: string; // 2–3 sentence "what the company does" → deals.business
  businessDetail?: BusinessDetail; // sectioned narrative (each section carries its sourceUrl) → deals.businessDetail
  gstin?: string; // → intel.registry.gstin
  pan?: string; // → intel.registry.pan
  cin?: string; // → intel.registry.cin
  creditRating?: string; // e.g. "CRISIL BBB+ / Stable" → intel.rating
  gstStatus?: string; // → intel.compliance.gst
  rocStatus?: string; // → intel.compliance.roc
  litigationNote?: string; // one-line summary → intel.litigation.note
  facilities?: string; // → deals.facilities
  employees?: string; // → deals.employees
  founded?: string; // → deals.founded
  incorporationDate?: string; // → intel.registry.incorporationDate (+ 4-digit year → deals.founded)
  companyType?: string; // e.g. "Private", "Public" → intel.registry.companyType
  registeredOffice?: string; // registered office / HQ → intel.registry.registeredAddress
  customers: { name: string; note?: string }[]; // → deals.customers (pct left 0 — never invented)
  products: { name: string }[]; // → deals.products
  certifications: string[]; // ISO/BIS/defence certs etc. → intel.strengths (only if empty)
}

/** Per-step outcome of one auto-populate orchestration run (autopopulate_runs.result). */
export interface AutopopulateResult {
  steps: Record<string, { status: 'done' | 'error' | 'skipped'; detail?: string }>;
}
export type AutopopulateStatus = 'running' | 'done' | 'error';

/** One year of a peer's profit-loss series (screener #profit-loss), oldest→newest. */
export interface PeerTrendRow {
  y: string; // period label, e.g. "Mar 2024"
  rev: number | null; // Sales / Revenue, ₹ crore
  eb: number | null; // Operating Profit (EBITDA proxy), ₹ crore
  pat: number | null; // Net Profit, ₹ crore
  margin: number | null; // %, computed = pat / rev
}

/** Listed-peer fundamentals scraped from screener.in (docs/adr/screener-source.md). */
export interface PeerFundamentals {
  name: string;
  ticker: string; // screener url path
  price: number | null; // current market price (CMP), ₹ per share
  marketCap: number | null; // ₹ crore
  pe: number | null;
  revenue: number | null; // ₹ crore, latest column
  netProfit: number | null; // ₹ crore, latest column
  netMargin: number | null; // %, computed = netProfit / revenue
  about?: string | null; // company "About" blurb from the screener page (optional)
  trend?: PeerTrendRow[]; // 3–5 year P&L series (oldest→newest), from #profit-loss (optional)
  ratios?: PeerRatio[]; // ALL of screener's top-ratios (Book Value, ROCE, ROE, Div Yield, 52w H/L…)
}

/** One label→value ratio from screener's "top ratios" panel (verbatim, e.g. "ROCE" → "18.4 %"). */
export interface PeerRatio {
  label: string;
  value: string;
}

/** IPO details from Chittorgarh (recent IPOs ~2007+; blank for older listings). */
export interface IpoDetails {
  ipoYear: string;
  brlm: string; // book-running lead managers
  peList: string; // P/E at listing (if the page states it)
  issuePrice: number | null; // ₹ per share
  issueSize?: string; // total issue size (e.g. "₹1,171 Cr")
  subscription?: string; // overall subscription (e.g. "45.2x")
  listingGain?: string; // listing-day gain vs issue price (e.g. "34%")
  checked?: boolean; // true once a Chittorgarh fetch was attempted (blank fields ⇒ pre-2007 / no page)
}

/** Which cascade hop supplied a value: 1 Chittorgarh (primary) · 2 NSE/BSE · 3 SEBI RHP. */
export type IpoHop = 1 | 2 | 3;

/** IPO listing board. `other`/`none` cover demerger / scheme-of-arrangement / direct listing. */
export type IpoBoard = 'mainboard' | 'sme' | 'other' | 'none';

/**
 * One IPO-history field carrying its OWN cascade provenance. `value: null` ⇒ still unknown
 * (never fabricated). `source` is the page the value came from; `hop` is which cascade step
 * supplied it. This per-field provenance lets the UI show a source link beside each number.
 */
export interface IpoField<T> {
  value: T | null;
  source: string | null; // source URL the value came from
  hop: IpoHop | null; // cascade hop that supplied it
  fetchedAt: string | null; // ISO
}

/**
 * Permanent per-ticker IPO history (ipo_history table — NO TTL, IPO facts are immutable).
 * Keyed on `ticker` (screener url path) / `isin`, NEVER the current name (companies rename).
 * Each field carries its own provenance so an IC memo can cite where every number came from.
 */
export interface IpoHistory {
  ticker: string; // screener url path — the permanent key
  isin: string | null;
  name: string; // canonical name AT FETCH TIME (may change later — do not key on it)
  ipoDate: IpoField<string>;
  ipoBoard: IpoField<IpoBoard>;
  issuePrice: IpoField<number>; // ₹ per share
  listingPrice: IpoField<number>; // ₹ on listing day
  listingGainPct: IpoField<number>; // listing-day gain vs issue price, %
  brlm: IpoField<string[]>; // book-running lead managers — ALWAYS a list (multiple is the norm)
  subscription: IpoField<string>; // overall subscription (e.g. "45.2x")
  issueSize: IpoField<string>; // total issue size (e.g. "₹1,171 Cr")
  peAtListing: IpoField<number>; // DERIVED = listing mcap ÷ trailing PAT; blank when inputs not confident
  peAtListingBasis: string | null; // formula + as-of note, shown when peAtListing is present
  label: string | null; // edge-case label (pre-db / demerger / SME) — shown instead of a bare "—"
  fetchedAt: string; // ISO — when the whole record was assembled
}

export interface PeerRow {
  name: string;
  ticker?: string; // screener url path → https://www.screener.in{ticker} (jsonb, no migration)
  about?: string | null; // business description from screener, shown as a one-liner under the name
  price?: number | null; // current market price (CMP), ₹ per share
  rev: number;
  mcap: number;
  ipoYear: string;
  brlm: string;
  peList: string;
  pe: number;
  margin: number;
  issuePrice?: number | null;
  issueSize?: string;
  subscription?: string;
  listingGain?: string;
  ipoChecked?: boolean; // Chittorgarh fetch attempted; empty ipoYear ⇒ "listed pre-2007 / no page"
  ipoHist?: IpoHistory | null; // full cascade record (per-field provenance + edge-case labels)
  ratios?: PeerRatio[]; // ALL of screener's top-ratios, shown verbatim in the drill-down (jsonb, no migration)
}

/** One recent headline for a peer (from the monitoring news_cache). */
export interface PeerHeadline {
  headline: string;
  source: string | null;
  url: string | null;
  publishedAt: string | null;
}

/** Full per-peer drill-down payload (screener + chittorgarh + news), cached 24h. */
export interface PeerDetail {
  name: string;
  ticker: string;
  about: string | null;
  pe: number | null;
  marketCap: number | null;
  revenue: number | null;
  netMargin: number | null;
  trend: PeerTrendRow[];
  ipo: IpoDetails | null;
  ipoHist?: IpoHistory | null; // full IPO cascade record (per-field provenance + edge-case labels)
  headlines: PeerHeadline[];
}

export interface Director {
  name: string;
  din: string;
  role: string; // designation, e.g. "Director" / "Managing Director"
  appointed?: string; // date of appointment / tenure, if disclosed
  source?: string; // 'MCA' | 'Deck' | 'Web' | 'Probe42'
}

export interface Shareholder {
  holder: string;
  pct: number;
}

/** Company-registry facts (from MCA aggregators like Tofler, or the licensed Probe42 API). See
 *  apps/api/src/registry.ts and apps/api/src/probe42.ts. */
export interface RegistryInfo {
  cin?: string;
  llpin?: string; // LLP identification number (Probe42 keys LLPs by this)
  pan?: string;
  gstin?: string;
  roc?: string;
  companyType?: string; // e.g. "Private", "Public"
  email?: string; // registered company email
  website?: string;
  incorporationDate?: string;
  registeredAddress?: string;
  authorisedCapital?: string;
  paidUpCapital?: string;
  companyStatus?: string;
  listingStatus?: string; // "Listed" | "Unlisted" (distinct from companyStatus, which is efiling/active status)
  cirpStatus?: string; // Corporate Insolvency Resolution Process status — present ⇒ a live insolvency proceeding
  lei?: string; // Legal Entity Identifier (LEI) number, if issued
  lastAgm?: string; // date of last AGM, if disclosed
  openCharges?: number; // count of open (registered) charges
  source?: string; // clickable URL to the aggregator page the data came from (Tofler, …) — NEVER a bare
                    // provenance label; UI treats this as a link, so a non-URL value renders broken.
                    // Provenance for a licensed-registry (Probe42) pull is the deal's `intel.probe42` marker.
  fetchedAt?: string; // ISO
}

/** A leadership person surfaced from the open web (company site / news / LinkedIn). */
export interface Leader {
  name: string;
  role: string;
  bg: string; // one-line background
  source: 'Web';
  sourceUrl: string;
}

/** One file inside a data room, hosted on the Shared Drive (referenced by link, not stored by us). */
export interface DataRoomFile {
  name: string;
  url: string; // Google Drive webViewLink
  category?: string; // deck | financials | legal | other
  id?: string; // Google Drive file id (for re-extract; falls back to parsing the url)
  extracted?: boolean; // true once this file's data has been read into the deal
  needsOcr?: boolean; // scanned/image file with no text layer — skipped by the auto-extract batch,
                      // left for the per-file browser OCR (↻). Prevents a scheduled batch from
                      // re-downloading it forever and never reaching "done".
  crashed?: boolean; // the Worker died (resource limit / memory) while parsing this file — written
                     // BEFORE the parse and cleared on any outcome that returns, so a leftover true
                     // means "this file kills the extractor", distinct from a scanned (needsOcr) file.
  crashCount?: number; // how many times it was in flight when the Worker died; quarantined at 2 (one kill can be transient).
}
/** A WhatsApp-delivered data room: the Shared Drive folder + its file manifest (deals.intel.dataroom). */
export interface DataRoom {
  url: string; // Shared Drive folder link
  files: DataRoomFile[];
  fetchedAt: string; // ISO
  filename?: string; // original zip name
  fileCount?: number; // set on the LIST payload when `files` is trimmed out (see /api/deals)
}

/** One PUBLISHED rating from a credit-rating agency (CRISIL/ICRA/CARE/India Ratings/…), from the
 *  agency's own public rationale/press release — NOT our internal financial-model grade. */
export interface AgencyRating {
  agency: string; // CRISIL | ICRA | CARE | India Ratings | Acuité | Brickwork | Infomerics
  longTerm?: string; // e.g. "CRISIL A-/Positive"
  shortTerm?: string; // e.g. "CRISIL A2+"
  outlook?: string; // Stable | Positive | Negative | Watch (often embedded in longTerm)
  action?: string; // Assigned | Reaffirmed | Upgraded | Downgraded | Withdrawn
  amount?: string; // total facilities / instrument size, e.g. "₹200 Crore"
  instrument?: string; // e.g. "Long-term bank facilities"
  date?: string; // rating date as published
  url: string; // link to the rationale / press release
}
/** Published agency ratings for a company (deals.intel.agencyRatings), web-fetched + provenanced. */
export interface AgencyRatings {
  ratings: AgencyRating[]; // latest per agency; empty = none found in the public domain
  fetchedAt: string; // ISO
  note?: string; // e.g. "No published agency rating found"
}

export interface Intel {
  rating: string;
  charges: number;
  agencyRatings?: AgencyRatings; // published CRISIL/ICRA/CARE/… ratings from the public domain
  compliance: { gst: string; tds: string; pf: string; roc: string };
  litigation: { sc: number; hc: number; trib: number; note: string };
  directors: Director[];
  shareholding: Shareholder[];
  registry?: RegistryInfo;
  leadership?: Leader[]; // web-found founders / CXOs / board, with source links
  dataroom?: DataRoom; // WhatsApp-delivered data room on the Shared Drive (link + file manifest)
  probe42?: Probe42Meta; // marker that a paid Probe42 pull exists (full payload lives in probe42_cache)
  strengths: string[];
  weaknesses: string[];
  adverse: string[];
}

/** Lightweight marker stamped on deals.intel when a Probe42 comprehensive pull has been made. The
 *  full (large) payload is NOT stored on the deal row — it lives in the probe42_cache table and is
 *  read on demand (free) — so a paid pull is never repeated and the deal row stays lean. */
export interface Probe42Meta {
  entityType: 'company' | 'llp' | 'pnp';
  identifier: string; // the CIN / LLPIN / PAN used for the pull
  fetchedAt: string; // ISO — when WE pulled it from Probe42
  lastUpdated?: string; // Probe's own last_updated / last_details_updated for the data
  sandbox?: boolean; // true while on the sandbox API
}

/** The Probe42 comprehensive payload we cache + render. Structured sections are best-effort (the API
 *  returns 800+ fields); `raw` always holds the complete JSON so nothing is lost. Mirrors the API's
 *  own sectioning (Key Statistics, Financials, Charges, Directors, Compliance, Documents, …). */
export interface Probe42Data extends Probe42Meta {
  legalName?: string;
  overview?: Record<string, unknown>;
  keyStatistics?: Record<string, unknown>;
  financials?: unknown;
  charges?: unknown;
  directors?: unknown;
  shareholding?: unknown;
  compliance?: unknown;
  ratings?: unknown;
  documents?: unknown;
  raw: unknown; // the complete comprehensive-details JSON object from Probe42
}

/** Result of a registry (MCA/Tofler) lookup — cached in registry_cache, merged into Intel. */
export interface RegistryLookup {
  cin: string | null;
  directors: Director[];
  shareholding: Shareholder[];
  info: RegistryInfo;
  leadership?: Leader[]; // web pass (cached alongside registry data)
  sourceUrl: string | null;
}

/** A news article naming a person (per-person research pass). */
export interface PersonNews {
  title: string;
  url: string;
  source: string; // publisher / hostname
  date: string; // as stated, else ''
}
/** An adverse signal on a person — NEVER asserted without a citation. */
export interface PersonAdverse {
  note: string;
  sourceUrl: string;
}
/**
 * Per-person research depth (deals.teamResearch.people[]) — the analyst-grade read on ONE person:
 * background, tenure, other directorships, news mentions, adverse signals and a bio link. Every
 * populated field is grounded in a real fetched source; unknowns stay absent ("not found"), never
 * a hallucinated bio.
 */
export interface PersonDepth {
  name: string; // matches the unified person by normalised name
  background?: string; // prior companies/roles, years of experience, functional domain, education
  tenure?: string; // at this company since when; founder vs professional hire vs promoter nominee
  otherDirectorships?: string[]; // other companies (from the DIN register or web)
  news?: PersonNews[]; // recent articles naming the person
  adverse?: PersonAdverse[]; // litigation / regulatory / disqualification / defaults — cited
  linkedin?: string | null; // LinkedIn / company-bio URL where found
  sourceUrl?: string | null; // primary source for the background line
  notFound?: boolean; // true = searched, nothing on the web (honest empty, not a blank)
}
/** Promoter view — who the promoters are, holding, and a deck-vs-registry cross-check. */
export interface PromoterView {
  promoters?: string[]; // named promoters / promoter group
  promoterHoldingPct?: number | null; // promoter holding from the registry shareholding, if disclosed
  deckPromoterStake?: number | null; // the deck's stated promoterStake, for the cross-check
  mismatch?: string | null; // set when deck vs registry holding differ materially (computed, not LLM)
  pledged?: string | null; // pledged shares, if disclosed
  note?: string | null; // one-line promoter summary
  sourceUrl?: string | null;
}
/** One evidenced synthesis statement — a short claim with its source, never a paragraph of adjectives. */
export interface TeamSynthesisItem {
  text: string;
  sourceUrl?: string | null;
}
/** Analyst-facing team synthesis that actually drives the invest/pass call on the team. */
export interface TeamSynthesis {
  strength?: TeamSynthesisItem[]; // domain depth, prior scale-up/exit, bench completeness + gaps
  keyManRisk?: TeamSynthesisItem[]; // which single departure would break the company
  governance?: TeamSynthesisItem[]; // family board, no independents, director churn, auditor changes
}
/** Per-person research + promoter view + synthesis (deals.teamResearch). Cached; re-runnable. */
export interface TeamResearch {
  people: PersonDepth[];
  promoter?: PromoterView;
  synthesis?: TeamSynthesis;
  fetchedAt: string; // ISO
}

/** A titled group of diligence questions to ask the promoter (Questions tab). */
export interface QuestionCategory {
  title: string; // e.g. "Financials", "Customer concentration", "Manufacturing & capacity"
  questions: string[];
}
/**
 * Deck-derived questions the panel should ask the promoter on the call (deals.promoterQuestions).
 * Generated from the deck but DELIBERATELY EXCLUDING anything the deck already answers — only the
 * gaps worth asking. Categorized so the analyst can scan by theme. Never fabricated filler.
 */
export interface PromoterQuestions {
  categories: QuestionCategory[];
  fetchedAt: string; // ISO
}

/**
 * LLM-generated overall read on a deal (deals.llmScore) — computed AFTER all enrichment lands,
 * over the full picture (deck + business + research + registry + industry + peers + team +
 * financials + valuation). Complements the deterministic engine Krit Score; never replaces it.
 */
export interface LlmScore {
  score: number; // 0-100
  verdict: string; // "Invest" | "Watch" | "Pass" (the model's call)
  pros: string[]; // evidenced strengths
  cons: string[]; // evidenced concerns / gaps
  rationale: string; // 2-4 sentence overall read
  fetchedAt: string; // ISO
}

/**
 * One auto-populate pass, keyed the same way on the server (route) and the client (sequencer).
 * See `autopop.ts` for the pure "should this pass run?" logic both sides share.
 */
export type AutopopStepKey = 'deck' | 'briefing' | 'questions' | 'registry' | 'team' | 'industry' | 'peers' | 'research' | 'score' | 'moduleRatings';

/**
 * How a pass ended:
 * - `filled` — it produced data and the deal was updated.
 * - `empty`  — it ran to completion but had nothing groundable to produce (no deck text, no
 *              people to research, the model returned nothing usable). Legitimate, not a failure.
 * - `error`  — it threw / an upstream returned an error, so nothing was written.
 */
export type AutopopAttemptOutcome = 'filled' | 'empty' | 'error';

/** A single recorded attempt at one pass. */
export interface AutopopAttempt {
  at: string; // ISO — when the attempt finished
  outcome: AutopopAttemptOutcome;
  note?: string; // short reason, e.g. 'no-deck-or-empty' / 'no-people' / an upstream message
}

/**
 * Per-deal ledger of auto-populate attempts (deals.autopopAttempts), one entry per pass key.
 *
 * WHY this exists: the passes are blanks-only, so "has this pass already run?" used to be inferred
 * from "is its output column non-null?". That inference is wrong for a pass that ran and legitimately
 * produced NOTHING — its column stays null, so first-open auto-populate re-fired on EVERY open of
 * that deal, forever (re-spending on each null pass and showing the loader every time). The ledger
 * records the attempt itself, so a pass that has already run is never auto-retried; the manual
 * "↻ Auto-populate" button still re-runs everything.
 */
export type AutopopAttempts = Partial<Record<AutopopStepKey, AutopopAttempt>>;

export interface Analyst {
  thesis: string;
  highlights: string[];
  concerns: string[];
  questions: string[];
}

export interface CallAnalysis {
  summary: string;
  keyPoints: string[];
  commitments: string[];
  redFlags: string[];
  followUps: string[];
  sentiment: string;
  /** Set to 'heuristic' when the AI step failed and this is only the keyword-based fallback summary. */
  source?: 'heuristic';
}

export type CallStatus = 'uploaded' | 'transcribing' | 'analysed';

export interface Call {
  id?: string;
  title: string;
  date: string;
  participants: string;
  duration: string;
  status: CallStatus;
  file?: string;
  transcript?: string;
  recordingUrl?: string; // link to the audio/video recording, for listening back to tone
  analysis: CallAnalysis | null;
}

export interface LogEntry {
  u: string;
  t: string;
  at: string;
}

export interface DocumentRef {
  id?: string;
  name: string;
  type?: string;
  pages?: number;
  chars?: number;
}

export interface Holding {
  invested: number;
  entryDate: string;
  entryVal: number;
  mark: number;
  ownership: number;
  ipoStage: string;
  ipoPct: number;
  milestone: string;
}

/** A pipeline deal — the central entity. */
export interface Deal {
  id: string;
  company: string;
  short: string;
  sector: string;
  location: string;
  status: Status;
  banker: string;
  poc: string;
  dateCRM: string;

  // financials (₹ crore)
  rev26: number;
  rev25: number;
  pat26: number;
  pat25: number;
  ebitda26: number;
  /** Detailed data-room statements (P&L + balance sheet + cash flow, multi-year). Null until extracted. */
  financials: FinancialStatements | null;

  // deal terms (₹ crore)
  valuationAsk: number;
  totalRaise: number;
  allocation: number;

  // valuation anchors
  peNow: number;
  peIPO: number;
  ipo: string;

  // quality metrics
  de: number;
  ocfPat: number;
  wcDays: number;
  promoterStake: number;
  top3: number;

  // narrative
  tailwinds: string;
  industry: string; // short one-line summary (fallback / cards)
  subIndustry: string; // precise free-form sub-industry classification
  industryDetail: IndustryDetail | null; // deep-dive India-specific industry & market analysis
  business: string; // short summary
  businessDetail: BusinessDetail | null; // multi-section narrative (+ per-section source)
  businessBrief: BusinessBrief | null; // deck-mined briefing: timeline, product table, plans, key numbers, open questions
  teamResearch: TeamResearch | null; // per-person depth + promoter view + team synthesis (Team & Mgmt tab)
  promoterQuestions: PromoterQuestions | null; // deck-derived diligence questions for the promoter call
  llmScore: LlmScore | null; // LLM overall read (score + pros/cons), computed after enrichment; complements the engine score
  moduleRatingsAI: ModuleRatingsAI | null; // AI-suggested module ratings + per-module reasons + a team-facing note; fills the sliders, never writes deals.ratings directly
  autopopAttempts: AutopopAttempts | null; // per-pass attempt ledger — stops first-open auto-populate re-firing forever
  provenance: Record<string, ProvenancedField> | null; // per-field source for profile fields
  research: ResearchDossier | null; // open-ended web-research dossier (auto-populated, provenanced)
  callsTrend: CallsTrend | null; // industry-trend synthesis across the deal's + competitors' calls
  internalCallsTrend: CallsTrend | null; // progress-tracking synthesis across ONLY the deal's own management calls
  use: string;

  // profile
  founded: string;
  employees: string;
  facilities: string;
  promoter: string;
  ipoTarget: string;

  // flags
  dataroom: boolean;
  deck: boolean;

  // scoring
  ratings: Ratings;

  // sub-tables
  peers: string[];
  peerRows: PeerRow[];
  customers: Customer[];
  products: Product[];
  team: TeamMemberBio[];
  pnl: PnlRow[];
  risks: Risk[];
  diligence: DiligenceItem[];
  intel: Intel;
  analyst: Analyst;
  documents: DocumentRef[];
  calls: Call[];
  log: LogEntry[];

  notes: string;
  holding: Holding | null;
}

/** Fund-level settings (settings singleton). */
export interface FundSettings {
  name: string;
  user: string;
  avatar: string;
  intakeNumber: string;
  transcribeKey: string;
  icThreshold: number;
}

/** Result of the valuation engine. */
export interface Valuation {
  pat: number;
  ask: number;
  peNow: number;
  peIPO: number;
  entryPE: number;
  mos: number;
  g: number;
  yrs: number;
  ok: boolean;
  base: number;
  bull: number;
  bear: number;
}

export type ReasonKind = 'ok' | 'warn' | 'bad';
export type Reason = [ReasonKind, string];

export interface ThesisFit {
  verdict: ThesisVerdict;
  c: string;
  reasons: Reason[];
}

export interface Grade {
  g: string;
  c: string;
}

export interface Recommendation {
  c: 'pass' | 'cond' | 'fail';
  i: string;
  h: string;
  s: string;
}

export interface ProjectionRow {
  y: string;
  rev: number;
  eb: number;
  pat: number;
}

export interface DiligenceStats {
  total: number;
  flag: number;
  open: number;
  cleared: number;
}

/** A research-repository entry — team knowledge, searchable and optionally linked to a deal. */
export type ResearchKind = 'note' | 'report' | 'link';
export interface ResearchNote {
  id: string;
  title: string;
  body: string;
  kind: ResearchKind;
  sector: string | null;
  tags: string[];
  sourceUrl: string | null;
  dealId: string | null;
  deal?: { id: string; short: string; company: string } | null; // hydrated for display
  author: { id: string; name: string; initials: string } | null;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

/** One factor's contribution to the computed credit rating. */
export interface CreditFactor {
  key: string;
  label: string;
  band: 'strong' | 'ok' | 'weak';
  detail: string; // human-readable value, e.g. "D/E 0.4x" or "no data"
  available: boolean; // false → excluded from the score (weight redistributed)
}

/**
 * Deterministic, computed creditworthiness for a company — a CRISIL-style letter grade derived
 * from the deal's financials + risk fields (NOT an external agency rating; that's intel.rating).
 * `rated: false` (grade "NR") when there's too little financial data to be meaningful.
 */
export interface CreditRating {
  rated: boolean;
  grade: string; // AAA | AA | A | BBB | BB | B | C | D | NR
  score: number; // 0–100
  color: string; // display tone (green/teal/amber/red/gray)
  investmentGrade: boolean; // BBB and above
  outlook: 'positive' | 'stable' | 'negative' | null;
  rationale: string; // one-line plain-English summary
  factors: CreditFactor[];
  coverage: number; // 0–1 share of factor weight that had data
}
